/**
 * ArticleImage
 * TipTap node for an Article Image inside a Help Article body: a `figure`
 * holding the `img` (referenced by `data-image-id`) and an optional
 * `figcaption`, with Image Placement and Image Size as data attributes on the
 * figure. See articleImageHelpers.ts for the exact markup contract.
 *
 * The node is an atom: Caption, Placement and Size are edited through the
 * image menu (ArticleImageMenu), never by typing inside the figure, so the
 * caption cannot pick up formatting the sanitizers would strip.
 *
 * `src` is kept only so the editor can display the image. The server swaps in
 * a fresh SAS URL whenever it returns an article and strips it again on save
 * (srg-backend ADR 0004), so emitting it in getHTML() is harmless.
 */
import { Node } from '@tiptap/core';
import type { ImagePlacement, ImageSize } from '@app-types';
import {
  DEFAULT_IMAGE_PLACEMENT,
  getDefaultImageSize,
  isArticleImageId,
  isImagePlacement,
  isImageSize
} from '../articleImageHelpers';

export const ARTICLE_IMAGE_NODE = 'articleImage';

export interface ArticleImageAttributes {
  imageId: string;
  src: string | null;
  caption: string;
  placement: ImagePlacement;
  size: ImageSize;
}

const findImg = (element: HTMLElement): HTMLImageElement | null => element.querySelector('img');

const parsePlacement = (element: HTMLElement): ImagePlacement => {
  const value = element.getAttribute('data-placement');
  return isImagePlacement(value) ? value : DEFAULT_IMAGE_PLACEMENT;
};

/**
 * Captions are visible text mirrored into `alt`. The figcaption wins because
 * it is what readers saw; `alt` covers markup whose figcaption was lost.
 */
const parseCaption = (element: HTMLElement): string => {
  const figcaption = element.querySelector('figcaption')?.textContent;
  const alt = findImg(element)?.getAttribute('alt');
  return (figcaption ?? alt ?? '').trim();
};

export const ArticleImage = Node.create({
  name: ARTICLE_IMAGE_NODE,
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    // Every attribute is rendered by renderHTML below, which places each one
    // on the right element of the figure/img/figcaption structure.
    return {
      imageId: {
        default: null,
        rendered: false,
        parseHTML: (element) => findImg(element)?.getAttribute('data-image-id') ?? null
      },
      src: {
        default: null,
        rendered: false,
        parseHTML: (element) => findImg(element)?.getAttribute('src') ?? null
      },
      caption: {
        default: '',
        rendered: false,
        parseHTML: (element) => parseCaption(element)
      },
      placement: {
        default: DEFAULT_IMAGE_PLACEMENT,
        rendered: false,
        parseHTML: (element) => parsePlacement(element)
      },
      size: {
        default: getDefaultImageSize(DEFAULT_IMAGE_PLACEMENT),
        rendered: false,
        // A missing or invalid size falls back to the Placement's default.
        parseHTML: (element) => {
          const value = element.getAttribute('data-size');
          return isImageSize(value) ? value : getDefaultImageSize(parsePlacement(element));
        }
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'figure',
        // Only figures referencing an uploaded image become Article Images;
        // foreign images are re-uploaded by ArticleImageUpload instead.
        getAttrs: (element) => (isArticleImageId(findImg(element)?.getAttribute('data-image-id')) ? null : false)
      }
    ];
  },

  renderHTML({ node }) {
    const { imageId, src, caption, placement, size } = node.attrs as ArticleImageAttributes;
    const imgAttributes: Record<string, string> = { 'data-image-id': imageId, alt: caption };
    if (src) imgAttributes.src = src;

    const figureAttributes = { 'data-placement': placement, 'data-size': size };
    return caption
      ? ['figure', figureAttributes, ['img', imgAttributes], ['figcaption', {}, caption]]
      : ['figure', figureAttributes, ['img', imgAttributes]];
  }
});

export default ArticleImage;
